import React from "react";
import { Card, CardContent } from "./ui/card";
import { Heart, Shield, Home } from "lucide-react";

export const Principles = () => {
  const principles = [
    {
      icon: <Heart className="h-10 w-10" />,
      title: "Humanity",
      tagline: "At the heart of everything we do is our shared humanity.",
      description: "We care for people with kindness, patience, and respect, recognising the individual behind every need and every moment of care.",
      color: "orange"
    },
    {
      icon: <Shield className="h-10 w-10" />,
      title: "Trust",
      tagline: "Trust is built through consistency, honesty, and responsibility.",
      description: "We do what we say we will do, support one another, and create a safe environment where residents, families, and staff feel confident and reassured.",
      color: "teal"
    },
    {
      icon: <Home className="h-10 w-10" />,
      title: "Belonging",
      tagline: "Our homes are places where people truly belong.",
      description: "Residents, families, and staff are welcomed, valued, and included, creating a sense of home, connection, and community.",
      color: "orange"
    }
  ];

  return (
    <section id="principles" className="py-20 bg-gradient-to-b from-white to-teal-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_github-pull-8/artifacts/1ub08pix_ChatGPT%20Image%20Apr%2028%2C%202026%2C%2002_38_32%20PM.png" 
              alt="Cozee Care" 
              className="h-12 w-12 object-contain"
            />
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Our Principles at Cozee Care
            </h2>
          </div>
          <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            The values that guide everything we do, every day
          </p>
        </div>

        {/* Principles Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {principles.map((principle, index) => (
            <Card
              key={index}
              className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden group relative"
            >
              {/* Decorative top border */}
              <div className={`h-2 ${principle.color === 'orange' ? 'bg-orange-500' : 'bg-teal-600'}`}></div>
              
              <CardContent className="pt-8 pb-8 px-6">
                {/* Icon */}
                <div className={`${principle.color === 'orange' ? 'bg-orange-100 text-orange-600' : 'bg-teal-100 text-teal-700'} w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {principle.icon}
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">
                  {principle.title}
                </h3>

                {/* Tagline */}
                <p className={`${principle.color === 'orange' ? 'text-orange-700' : 'text-teal-700'} font-semibold text-center mb-4 italic`}>
                  {principle.tagline}
                </p>

                {/* Description */}
                <p className="text-gray-600 text-center leading-relaxed">
                  {principle.description}
                </p>
              </CardContent>

              {/* Decorative corner accent */}
              <div className={`absolute bottom-0 right-0 w-24 h-24 ${principle.color === 'orange' ? 'bg-orange-50' : 'bg-teal-50'} rounded-tl-full opacity-30 -z-10`}></div>
            </Card>
          ))}
        </div>

        {/* Bottom Message */}
        <div className="text-center mt-16">
          <Card className="max-w-4xl mx-auto border-2 border-teal-200 bg-white shadow-lg">
            <CardContent className="pt-8 pb-8">
              <p className="text-lg text-gray-700 leading-relaxed">
                These principles aren't just words on a page. They're the foundation of how we work, 
                how we support each other, and how we create a genuine home for everyone at Barnston Court.
              </p>
              <div className="mt-4 flex items-center justify-center gap-2">
                <Heart className="h-5 w-5 text-orange-500 fill-orange-500" />
                <span className="text-teal-700 font-semibold">Caring with purpose, every single day</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
